import React, { useLayoutEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
    CheckCircle2,
    Clock3,
    Loader2,
    Utensils,
    XCircle,
} from "lucide-react";
import { NgwAvatar } from "../../components/common";
import { ApprovalHistory } from "../../components/notificationv2";
import { setTopNavNameTitle } from "../../redux/features/mobileLayout";
import { RootState, useAppDispatch } from "../../redux/store";
import { callPhpWorkflow } from "../../redux/features/common";
import { getNotificationDetail } from "../../redux/features/notifications";
import { DateTimeUtil } from "../../utils";
import {
    NotifDetailsV2WorkflowDetail,
    NotificationDetailV2,
    TimecardMealBreakDetails,
} from "../../types";

type ShiftAuditDetails = {
    shift_json?: {
        os_end_date?: string;
        os_shift_id?: string;
        os_shift_code?: string;
        os_shift_name?: string;
        os_start_date?: string;
    };
};

const formatDate = (value?: string | null, format = "MMM D, YYYY") => {
    if (!value) return "N/A";
    const phpFormat = format === "MMM D, YYYY" ? "M d, Y" : format;
    const formatted = DateTimeUtil.DisplayDateTime(value, phpFormat, "local", true);
    return formatted.startsWith("Invalid") ? "N/A" : formatted;
};

const formatTime = (value?: string | null) => {
    if (!value) return "N/A";
    const formatted = DateTimeUtil.DisplayDateTime(value, "h:i A", "local", true);
    return formatted.startsWith("Invalid") ? "N/A" : formatted;
};

const formatBackendDate = (value?: string | null, format = "MMM D, YYYY") => {
    if (!value) return "N/A";
    const phpFormat = format === "MMM D, YYYY" ? "M d, Y" : format;
    const formatted = DateTimeUtil.DisplayDateTime(value, phpFormat, "local", false);
    return formatted.startsWith("Invalid") ? "N/A" : formatted;
};

const formatBackendTime = (value?: string | null) => {
    if (!value) return "N/A";
    const formatted = DateTimeUtil.DisplayDateTime(value, "h:i A", "local", false);
    return formatted.startsWith("Invalid") ? "N/A" : formatted;
};

const formatAuditTime = (value?: string | null) => {
    if (!value) return null;
    const formatted = DateTimeUtil.prettyPrintTime(value, "");
    return formatted || null;
};

const parseShiftAudit = (auditFlag?: string | null): ShiftAuditDetails | null => {
    if (!auditFlag) return null;

    try {
        return JSON.parse(auditFlag) as ShiftAuditDetails;
    } catch {
        return null;
    }
};

const findRequestor = (
    requestorId: string | undefined,
    workflowDetails: NotifDetailsV2WorkflowDetail[] | undefined
) => {
    if (!workflowDetails?.length) return null;

    for (const step of workflowDetails) {
        const actors = Object.values(step.actor_json || {});
        const matchedActor = actors.find((actor) => actor.employee_id && actor.employee_id === requestorId);
        if (matchedActor) {
            return matchedActor;
        }
    }

    return Object.values(workflowDetails[0]?.actor_json || {})[0] ?? null;
};

const isCurrentUserInStepActors = (user: any, step?: NotifDetailsV2WorkflowDetail) => {
    if (!user || !step) return false;

    const currentUserIds = [user.id, user.userAccountId, user.os_emp_account_id, user.employee_id]
        .filter(Boolean)
        .map(String);

    if (!currentUserIds.length) return false;

    const actors = Object.values(step.actor_json || {});
    return actors.some((actor) => {
        const actorId = actor.employee_id;
        return actorId != null && currentUserIds.includes(String(actorId));
    });
};

const getMealBreakRequestNote = (workflowDetails?: NotifDetailsV2WorkflowDetail[]): string => {
    const requestNote = workflowDetails
        ?.map((workflow) => workflow.wf_task_acted_json?.REQUEST_NOTE?.trim())
        .find((note) => Boolean(note));

    return requestNote || "Not provided";
};

const MealBreakNotificationDetail: React.FC = () => {
    const notificationDetail = useSelector((state: RootState) => state.notification.notificationDetail as NotificationDetailV2 | undefined);
    const user = useSelector((state: RootState) => state.user.user as any);
    const isLoading = useSelector((state: RootState) => state.notification.isNotificationDetailLoading);
    const isCallingWorkflow = useSelector((state: RootState) => state.common.isCallingWorkflow);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [notes, setNotes] = useState("");

    const mealBreakDetails = notificationDetail?.timecard_meal_break_details;
    const workflowDetails = notificationDetail?.workflow_details;
    const requestorIdFromPayload =
        mealBreakDetails?.os_emp_account_id || notificationDetail?.alert_json?.os_emp_account_id;
    const requestor = useMemo(
        () => findRequestor(requestorIdFromPayload, workflowDetails),
        [requestorIdFromPayload, workflowDetails]
    );
    const shiftAudit = useMemo(() => parseShiftAudit(mealBreakDetails?.audit_flag), [mealBreakDetails?.audit_flag]);
    const currentStep = useMemo(() => {
        const currentStepCode = notificationDetail?.workflow_current_step_details?.step_code;
        return workflowDetails?.find((workflow) => workflow.step_code === currentStepCode);
    }, [notificationDetail?.workflow_current_step_details?.step_code, workflowDetails]);
    const requestorEmployeeId = requestorIdFromPayload || requestor?.employee_id;
    const referenceId = mealBreakDetails?.os_emp_schedules_id || notificationDetail?.alert_json?.REF_ID?.toString();
    const shiftStartedAt = mealBreakDetails?.os_emp_start_datetime || null;
    const submittedAt = mealBreakDetails?.created || null;
    const mealBreakReason = useMemo(() => getMealBreakRequestNote(workflowDetails), [workflowDetails]);
    const canTakeAction =
        currentStep?.current_status === "0" &&
        notificationDetail?.only_notify_no_action !== "1" &&
        isCurrentUserInStepActors(user, currentStep);

    const title =
        notificationDetail?.message_info?.title ||
        notificationDetail?.notification_display_name ||
        "Meal Break Request";
    const body = notificationDetail?.message_info?.body || "";

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle(title));
    }, [dispatch, title]);

    const refreshData = async () => {
        if (!notificationDetail?.notification_id || !user?.os_emp_account_id) return;

        setIsRefreshing(true);
        await dispatch(
            getNotificationDetail({
                notificationId: notificationDetail.notification_id,
                employeeId: Number(user.os_emp_account_id),
            })
        );
        setIsRefreshing(false);
    };

    const submitAction = async (status: 0 | 1) => {
        if (!notificationDetail?.alert_json?.WF_CODE || !requestorEmployeeId || !referenceId) return;

        const bodyPayload = {
            workflow_code: notificationDetail.alert_json.WF_CODE,
            status,
            current_datetime: DateTimeUtil.formatDateToMySQL(new Date()),
            // user_account_id: user?.userAccountId?.toString() ?? "",
            // actor_id: user?.id?.toString() ?? "",
            reference_id: referenceId,
            result:
                status === 1
                    ? { APPROVED: { [requestorEmployeeId]: [referenceId] }, REJECTED: {} }
                    : { APPROVED: {}, REJECTED: { [requestorEmployeeId]: [referenceId] } },
            notes: notes.trim(),
            workflow_header_id: notificationDetail.alert_json.WF_HEADER_ID,
            wf_txn_lines_id: notificationDetail.alert_json.WF_TXN_LINES_ID,
        };


        console.log("Submitting workflow action with payload:", bodyPayload);
        // return;

        await dispatch(callPhpWorkflow(bodyPayload));
        await refreshData();
    };

    const handleViewTimesheet = () => {
        if (!requestorEmployeeId) return;
        navigate(`/m-manager-timesheet/${requestorEmployeeId}`, {
            state: {
                from: "/notificationDetail",
                targetDate: mealBreakDetails?.os_emp_schedule_date || shiftStartedAt || submittedAt,
            },
        });
    };

    const shiftName = shiftAudit?.shift_json?.os_shift_name;
    const shiftStart = formatAuditTime(shiftAudit?.shift_json?.os_start_date);
    const shiftEnd = formatAuditTime(shiftAudit?.shift_json?.os_end_date);
    const shiftSummary =
        shiftName && shiftStart && shiftEnd
            ? `${shiftName} (${shiftStart} - ${shiftEnd})`
            : shiftName || [shiftStart, shiftEnd].filter(Boolean).join(" - ") || null;
    const metadataItems = [
        shiftSummary ? `Shift: ${shiftSummary}` : null,
        mealBreakDetails?.shift_id ? `Shift ID: ${mealBreakDetails.shift_id}` : null,
        mealBreakDetails?.department_code ? `Department: ${mealBreakDetails.department_code}` : null,
    ].filter(Boolean) as string[];
    const isBusy = isCallingWorkflow || isRefreshing;

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-40 w-full">
                <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
            </div>
        );
    }

    if (!notificationDetail) {
        return (
            <div className="bg-gray-50 min-h-screen pb-8">
                <div className="w-full md:w-1/2 mx-auto px-4 pt-4">
                    <div className="rounded-lg bg-white p-4 shadow-sm text-sm text-gray-600">
                        Meal break details are not available for this notification.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen pb-8">
            <div className="w-full md:w-1/2 mx-auto">
                <div className="bg-white mx-4 mt-4 rounded-lg shadow-sm p-4 mb-3">
                    <div className="mt-5 flex items-center gap-3">
                        <div className="h-14 w-14 flex-shrink-0">
                            <NgwAvatar src={requestor?.image_url} name={requestor?.name} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xl font-semibold text-gray-900">{requestor?.name || "Employee"}</p>
                            {/* <div className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-gray-600"> */}
                                {/* {requestorEmployeeId && <span>Employee ID: {requestorEmployeeId}</span>} */}
                                {/* {requestorEmployeeId && mealBreakDetails?.department_code && <span>&bull;</span>} */}
                                {/* {mealBreakDetails?.department_code && <span>{mealBreakDetails.department_code}</span>} */}
                            {/* </div> */}
                            {/* <p className="mt-2 text-sm text-gray-500">
                                Requested on {formatDateTime(requestedOn)}
                            </p> */}
                        </div>
                    </div>
                </div>

                <div className="bg-white mx-4 rounded-lg shadow-sm p-4 mb-3">
                    <div className="rounded-xl border border-gray-200 p-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700">
                                <Utensils className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-green-700">Lunch Break Details</h2>
                                <p className="mt-1 text-sm text-gray-600">
                                    {body || "Meal break activity was submitted for this shift."}
                                </p>
                            </div>
                        </div>

                        <div className="mt-5 grid gap-4 border-t border-gray-200 pt-4 md:grid-cols-2">
                            <div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Clock3 className="w-4 h-4" />
                                    <span>Shift started at</span>
                                </div>
                                <p className="mt-2 text-xl font-semibold text-gray-900">
                                    {formatBackendTime(shiftStartedAt)}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                    {formatBackendDate(shiftStartedAt)}
                                </p>
                            </div>

                            <div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Clock3 className="w-4 h-4" />
                                    <span>Submitted at</span>
                                </div>
                                <p className="mt-2 text-xl font-semibold text-gray-900">
                                    {formatTime(submittedAt)}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                    {formatDate(submittedAt)}
                                </p>
                            </div>
                        </div>

                        <div className="mt-5 border-t border-gray-200 pt-4">
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Clock3 className="w-4 h-4" />
                                <span>Reason for skipping lunch break</span>
                            </div>
                            <p className="mt-2 whitespace-pre-wrap rounded-lg bg-gray-50 px-3 py-3 text-sm text-gray-700">
                                {mealBreakReason}
                            </p>
                        </div>

                        {/* {metadataItems.length > 0 && (
                            <div className="mt-5 border-t border-gray-200 pt-4">
                                <div className="flex flex-wrap gap-x-3 gap-y-2 text-sm text-gray-600">
                                    {metadataItems.map((item, index) => (
                                        <React.Fragment key={item}>
                                            {index > 0 && <span className="text-gray-300">&bull;</span>}
                                            <span>{item}</span>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        )} */}
                    </div>
                </div>

                <div className="bg-white mx-4 rounded-lg shadow-sm p-4 mb-3">
                    <ApprovalHistory
                        workflowDetails={workflowDetails}
                        title="Approval Workflow"
                        className="mt-0"
                    />
                </div>

                <div className="bg-white mx-4 rounded-lg shadow-sm p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <button
                            type="button"
                            onClick={handleViewTimesheet}
                            disabled={!requestorEmployeeId}
                            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            View Timesheet
                        </button>

                        {canTakeAction && (
                            <div className="w-full md:max-w-md">
                                <textarea
                                    value={notes}
                                    onChange={(event) => setNotes(event.target.value)}
                                    rows={3}
                                    className="min-h-[96px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                    placeholder="Add notes for the employee (optional)"
                                />
                                <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                                    <button
                                        type="button"
                                        onClick={() => submitAction(0)}
                                        disabled={isBusy}
                                        className="inline-flex items-center justify-center rounded-md border border-red-300 bg-white px-5 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {isBusy ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                <XCircle className="mr-2 w-4 h-4" />
                                                Reject
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => submitAction(1)}
                                        disabled={isBusy}
                                        className="inline-flex items-center justify-center rounded-md border border-blue-600 bg-white px-5 py-2.5 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {isBusy ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                <CheckCircle2 className="mr-2 w-4 h-4" />
                                                Approve
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MealBreakNotificationDetail;
